import { css } from '@inter-ikea-kompis/web-component';
import type { ITheme } from '@inter-ikea-kompis/types';

import PromptAnimationNameEnum from '../../enums/PromptAnimationNameEnum.js';

const MAX_WIDTH = 504;
type Options = {
	isFullscreen: boolean;
	zIndex: number;
	isAnimating: boolean;
	top: number;
	visible: boolean;
	bottom: number;
	compact: boolean;
	theme: ITheme;
};

export default class PromptStyle {
	/**
	 * Returns styles.
	 *
	 * @param options Options object.
	 * @param options.isFullscreen "true" if fullscreen.
	 * @param options.zIndex Z-index.
	 * @param options.state Lightbox state.
	 * @param options.top Offset from top.
	 * @param options.bottom Offset from bottom.
	 * @param options.theme Theme.
	 * @returns Styles.
	 */
	public static getStyle(options: Options): string {
		const { spacing, color, motion, breakpoint } = options.theme;
		return css`
			:host {
				pointer-events: none;
				position: fixed;
				left: 0;
				right: 0;
				top: 0;
				bottom: 0;
				z-index: ${options.zIndex};
			}
			* {
				z-index: inherit;
			}
			.backdrop {
				display: block;
				position: absolute;
				top: 0;
				left: 0;
				bottom: 0;
				right: 0;
				opacity: ${options.visible ? 1 : 0};
				transition-property: opacity;
				transition-duration: ${motion.duration.medium};
				background-color: ${color.transparency.light};
				pointer-events: auto;
			}
			.intersection {
				position: absolute;
				left: 0;
				right: 0;
				top: ${options.top}px;
				bottom: ${options.bottom}px;
				display: flex;
				transition:
					top ${motion.duration.medium} ${motion.ease.easy},
					bottom ${motion.duration.medium} ${motion.ease.easy};
			}
			.body {
				flex-grow: 1;
			}
			.footer {
				display: flex;
			}
			.scrollContainer {
				overflow-y: auto;
				flex-grow: 1;
				display: flex;
				flex-direction: column;
			}
			.buttonDivider {
				width: ${spacing.space75 / 16}rem;
				height: ${spacing.space100 / 16}rem;
				flex-shrink: 0;
			}
			.lightbox {
				pointer-events: ${options.isAnimating ? 'none' : 'auto'};
				overflow: hidden;
				position: relative;
				background-color: ${color.neutral.white};
				display: flex;
				flex-direction: column;
				${this.getAnimationName(options) ?? ''};
				animation-duration: ${options.theme.motion.duration.medium};
				animation-fill-mode: both;
				animation-timing-function: ${options.visible ? 'ease-out' : 'easy-in'};
			}
			@media screen and (max-width: ${breakpoint.medium.width / 16}rem) {
				${this.getMobileRules(options)}
			}
			@media screen and (min-width: ${(breakpoint.medium.width + 1) / 16}rem) {
				${this.getDesktopRules(options)}
			}
		`;
	}

	private static getAnimationName(options: Options): string | null {
		if (!options.isAnimating) {
			return null;
		}

		if (options.visible) {
			return css`
				animation-name: ${PromptAnimationNameEnum.opening};
			`;
		}
		return css`
			animation-name: ${PromptAnimationNameEnum.closing};
		`;
	}

	/**
	 * Gets the mobile rules for borders, opening, closing for sheet.
	 *
	 * @param options Options.
	 */
	private static getMobileRules(options: Options): string {
		const { spacing, border, color } = options.theme;
		const borderRadius = options.isFullscreen ? 0 : border.radius.radius8 / 16;
		const lightboxPaddingTop = options.isFullscreen ? 0 : spacing.space150 / 16;
		const headerMarginTop = options.isFullscreen ? spacing.space550 / 16 : spacing.space150 / 16;
		return css`
			.header {
				margin-top: ${headerMarginTop}rem;
				padding: 0 ${spacing.space150 / 16}rem;
				color: ${color.text.dark};
			}
			.body {
				margin-top: ${spacing.space100 / 16}rem;
				padding: 0 ${spacing.space150 / 16}rem;
				overflow-x: hidden;
			}
			.footer {
				margin: ${spacing.space150 / 16}rem 0;
				padding: 0 ${spacing.space150 / 16}rem;
				flex-direction: ${options.compact ? 'row-reverse' : 'column'};
			}
			.lightbox {
				flex-grow: ${options.isFullscreen ? 1 : 0};
				border-top-left-radius: ${borderRadius}rem;
				border-top-right-radius: ${borderRadius}rem;
			}
			.intersection {
				padding-top: ${lightboxPaddingTop}rem;
				flex-direction: column-reverse;
			}
			@keyframes ${PromptAnimationNameEnum.opening} {
				from {
					transform: translateY(100%);
				}
				to {
					transform: none;
				}
			}
			@keyframes ${PromptAnimationNameEnum.closing} {
				from {
					transform: none;
				}
				to {
					transform: translateY(100%);
				}
			}
		`;
	}

	/**
	 * Gets the desktop rules for borders, opening, closing for sheet.
	 *
	 * @param options Options.
	 */
	private static getDesktopRules(options: Options): string {
		const { spacing, border } = options.theme;
		return css`
			.header {
				margin-top: ${spacing.space250 / 16}rem;
				padding: 0 ${spacing.space150 / 16}rem;
			}
			.body {
				margin-top: ${spacing.space100 / 16}rem;
				padding: 0 ${spacing.space150 / 16}rem;
			}
			.footer {
				margin: ${spacing.space250 / 16}rem 0;
				padding: 0 ${spacing.space150 / 16}rem;
				flex-direction: ${options.compact ? 'row-reverse' : 'column'};
			}
			.lightbox {
				max-height: 75vh;
				width: ${MAX_WIDTH / 16}rem;
				max-width: ${MAX_WIDTH / 16}rem;
				border-radius: ${border.radius.radius8 / 16}rem;
			}
			.intersection {
				flex-direction: column;
				justify-content: center;
				align-items: center;
			}
			@keyframes ${PromptAnimationNameEnum.closing} {
				from {
					opacity: 1;
					transform: scale(1);
				}
				to {
					opacity: 0;
					transform: scale(0.9);
				}
			}
			@keyframes ${PromptAnimationNameEnum.opening} {
				from {
					opacity: 0;
					transform: scale(0.9);
				}
				to {
					opacity: 1;
					transform: scale(1);
				}
			}
		`;
	}
}
